import { api } from './client';

export interface SystemStats {
  hostname: string;
  distro: string;
  release: string;
  kernel: string;
  arch: string;
  uptime_seconds: number;
  timezone: string;
  current_time: string;
}

export interface DiskStat {
  mount: string;
  total: number;
  used: number;
  percent: number;
  warn: boolean;
  crit: boolean;
}

export interface ResourceStats {
  cpu_percent: number;
  ram: { total: number; used: number; percent: number };
  swap: { total: number; used: number; percent: number };
  disks: DiskStat[];
}

export interface HardwareStats {
  cpu: { model: string; cores: number; physical_cores: number; speed_ghz: number };
  ram_total: number;
  network_interfaces: Array<{
    name: string;
    ip4: string;
    ip6: string;
    mac: string;
    speed_mbps: number;
    type: string;
  }>;
}

export interface NetSample {
  ts: number;
  rx_sec: number;
  tx_sec: number;
}

export interface NetworkTrafficStats {
  samples: NetSample[];
}

export interface UpdatesStats {
  count: number;
  packages: string[];
  reboot_required: boolean;
  fetched_at: number;
}

export interface SshSession {
  user: string;
  tty: string;
  from: string | null;
  login_time: string;
}

export interface SshSessionsStats {
  sessions: SshSession[];
}

export const fetchSystemStats = (): Promise<SystemStats> =>
  api.get<SystemStats>('/stats/system').then((r) => r.data);

export const fetchResourceStats = (): Promise<ResourceStats> =>
  api.get<ResourceStats>('/stats/resources').then((r) => r.data);

export const fetchHardwareStats = (): Promise<HardwareStats> =>
  api.get<HardwareStats>('/stats/hardware').then((r) => r.data);

export const fetchNetworkTraffic = (): Promise<NetworkTrafficStats> =>
  api.get<NetworkTrafficStats>('/stats/network-traffic').then((r) => r.data);

export const fetchUpdates = (): Promise<UpdatesStats> =>
  api.get<UpdatesStats>('/stats/updates').then((r) => r.data);

export const fetchSshSessions = (): Promise<SshSessionsStats> =>
  api.get<SshSessionsStats>('/stats/ssh-sessions').then((r) => r.data);

export interface SysstatSample {
  timestamp: string;
  cpu: {
    totalUsagePct: number;
    userPct: number;
    systemPct: number;
    iowaitPct: number;
    stealPct: number;
  } | null;
  memory: {
    usedPct: number;
    usedMB: number;
    totalMB: number;
    swapUsedMB: number;
  } | null;
  disk: {
    readKBs: number;
    writeKBs: number;
    avgUtilPct: number;
  } | null;
  network: Array<{ iface: string; rxKBs: number; txKBs: number }>;
  loadAvg: { load1: number | null; load5: number | null; load15: number | null } | null;
}

export interface SysstatHistory {
  meta: {
    totalSamples?: number;
    oldestSample?: string;
    newestSample?: string;
    intervalMinutes?: number;
  };
  samples: SysstatSample[];
}

export const fetchSysstatHistory = (): Promise<SysstatHistory> =>
  api.get<SysstatHistory>('/stats/sysstat-history').then((r) => r.data);
